import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const mapSrc =
    "https://www.google.com/maps?q=Maslak,Sariyer,Istanbul&z=13&output=embed";

  return (
    <div className="relative z-10 bg-[#050505]" data-testid="contact-page">
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-40 pb-16 md:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="text-xs tracking-[0.25em] text-[#FF3B30] font-medium mb-8"
          data-testid="contact-eyebrow"
        >
          {c.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black tracking-tighter text-6xl sm:text-8xl lg:text-9xl leading-[0.85] text-white/95"
        >
          {c.title}
        </motion.h1>
        <Reveal delay={0.2}>
          <p className="mt-8 text-white/55 text-lg max-w-md leading-relaxed">{c.intro}</p>
        </Reveal>
      </section>

      {/* Big typographic contact */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20">
        <div className="border-t border-white/10">
          {/* Email */}
          <Reveal className="group grid md:grid-cols-12 items-center gap-4 py-10 border-b border-white/10">
            <div className="md:col-span-3 flex items-center gap-3 text-white/40 text-xs tracking-[0.25em]">
              <Mail size={16} /> {c.email_label.toUpperCase()}
            </div>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email"
              className="md:col-span-9 font-display font-black tracking-tighter text-3xl sm:text-5xl text-white/90 hover:text-[#FF3B30] transition-colors duration-300 inline-flex items-center gap-3"
            >
              {CONTACT.email}
              <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </a>
          </Reveal>

          {/* Phone */}
          <Reveal delay={0.05} className="group grid md:grid-cols-12 items-center gap-4 py-10 border-b border-white/10">
            <div className="md:col-span-3 flex items-center gap-3 text-white/40 text-xs tracking-[0.25em]">
              <Phone size={16} /> {c.phone_label.toUpperCase()}
            </div>
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="md:col-span-9 font-display font-black tracking-tighter text-3xl sm:text-5xl text-white/90 hover:text-[#FF3B30] transition-colors duration-300"
            >
              {CONTACT.phone}
            </a>
          </Reveal>

          {/* Address */}
          <Reveal delay={0.1} className="grid md:grid-cols-12 items-center gap-4 py-10 border-b border-white/10">
            <div className="md:col-span-3 flex items-center gap-3 text-white/40 text-xs tracking-[0.25em]">
              <MapPin size={16} /> {c.address_label.toUpperCase()}
            </div>
            <p className="md:col-span-9 font-display font-bold text-2xl sm:text-4xl text-white/90" data-testid="contact-address">
              {c.address}
            </p>
          </Reveal>

          {/* Hours */}
          <Reveal delay={0.15} className="grid md:grid-cols-12 items-center gap-4 py-10 border-b border-white/10">
            <div className="md:col-span-3 flex items-center gap-3 text-white/40 text-xs tracking-[0.25em]">
              <Clock size={16} /> {c.hours_label.toUpperCase()}
            </div>
            <p className="md:col-span-9 font-display font-bold text-2xl sm:text-4xl text-white/90">{c.hours}</p>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-32 md:pb-44">
        <Reveal className="rounded-3xl overflow-hidden border border-white/10 grayscale contrast-125">
          <iframe
            title="Aportta konum"
            src={mapSrc}
            data-testid="contact-map"
            className="w-full h-[380px] md:h-[460px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Reveal>
      </section>
    </div>
  );
}
